/**
 * The rail's destinations, and what a click on one does — read by the nav rail, by the phone's
 * list and by the home launcher, so the three offer the same places and open them the same way.
 * The launcher lays them out most important first (`launcherLayout`).
 *
 * The order is the rail's (`navOrder`), with the view half first: watching above the rule,
 * configuring below it.
 *
 * Camera settings is the one entry no panel supplies: it stands for a workflow — the form, the
 * cameras list it is configured from, and the live preview — rather than for a panel, so its
 * open is its own.
 *
 * The launcher shows a tile for an entry whose panel declares a `description`, since a tile has
 * to say what a place is for. A panel that declares `launcherTabs` gets one tile for each tab in
 * place of its own.
 */

import { getPanelEntry, listPanels } from '../registry';
import { CameraSettingsIcon } from '@/components/occurra/icons/CameraSettingsIcon';
import type {
    LauncherPlace, NavEntry, NavOpenState, OpenPanelFn, PanelConfig, PanelId,
} from '@/types/panels';
import { PanelMode } from '@/types/panels';

import { CHAT_PANEL_ID } from './ChatDrawer';

/** The cameras list: the entry point to the app, and what every camera workflow starts from. */
const PRIMARY_ID: PanelId = 'cameras_list';

/** The settings half leads with it, so cameras are reachable from both halves of the rail. */
const CAMERA_SETTINGS_ENTRY: NavEntry = {
    id: 'camera_settings',
    // Its panel header reads "Camera Settings"; the rail names it as it names every other entry.
    label: 'Camera settings',
    description: 'Set up a camera: its streams, its recording and its agents.',
    icon: CameraSettingsIcon,
    group: PanelMode.Settings,
};

function getEntry(panel: PanelConfig): NavEntry {
    return {
        id: panel.id,
        label: panel.title,
        description: panel.description,
        icon: panel.icon ?? null,
        group: panel.navGroup,
    };
}

/**
 * Every destination, view half first, each half in `navOrder`. The chat is left out — the logo
 * is its control — and so is an admin-only panel the member cannot reach.
 */
export function listNavEntries(canManage: boolean): NavEntry[] {
    const panels = listPanels()
        .filter((panel) => panel.hasNavButton && panel.id !== CHAT_PANEL_ID)
        .filter((panel) => !panel.requiresAdmin || canManage)
        .sort((firstPanelConfig, second) => firstPanelConfig.navOrder - second.navOrder);
    const isPrimary = (panel: PanelConfig): boolean => panel.id === PRIMARY_ID;
    const view = panels.filter((panel) => panel.navGroup === PanelMode.View);
    return [
        // Cameras heads the view half: nearly every workflow starts by picking a camera.
        ...view.filter(isPrimary).map(getEntry),
        ...view.filter((panel) => !isPrimary(panel)).map(getEntry),
        CAMERA_SETTINGS_ENTRY,
        ...panels
            .filter((panel) => !isPrimary(panel) && panel.navGroup === PanelMode.Settings)
            .map(getEntry),
    ];
}

/** The tiles of the home launcher, in rail order: each entry, or each tab of its panel. */
export function listLauncherPlaces(canManage: boolean): LauncherPlace[] {
    return listNavEntries(canManage)
        .filter((entry) => entry.description !== '')
        .flatMap((entry): LauncherPlace[] => {
            const tabs = (getPanelEntry(entry.id)?.config.launcherTabs ?? [])
                .filter((tab) => !tab.requiresAdmin || canManage);
            if (tabs.length === 0) {
                return [{
                    key: entry.id,
                    entryId: entry.id,
                    label: entry.label,
                    description: entry.description,
                    icon: entry.icon,
                }];
            }
            return tabs.map((tab) => ({
                key: `${entry.id}:${tab.key}`,
                entryId: entry.id,
                label: tab.label,
                description: tab.description,
                icon: tab.icon,
                props: tab.props,
            }));
        });
}

/**
 * Whether the entry's surface is the one the operator is on.
 *
 * Cameras and Camera settings are two sides of ONE workflow, so they read as a mode switch:
 * exactly one is lit. Cameras is lit only while the form is absent — with the form open the app
 * IS in settings mode, and lighting both would say the opposite. Open, not visible: on a narrow
 * stage the workflow shows its camera tree with the form behind it, and that is still the
 * settings side.
 */
export function isNavEntryOpen(id: PanelId, { visibleIds, openIds }: NavOpenState): boolean {
    if (id === 'camera_settings') {
        return openIds.has('camera_settings');
    }
    if (id === PRIMARY_ID) {
        return visibleIds.has(id) && !openIds.has('camera_settings');
    }
    return visibleIds.has(id);
}

/**
 * Opens the entry. An entry only ever opens: a click on the lit one leaves its panel alone,
 * since a panel is dismissed by its own ×, where the intent is unambiguous.
 *
 * A panel with tabs opens on its first tab, not on the tab left open last: the entry names the
 * first tab (Cameras, Bookmarks), and the other tabs have launcher tiles of their own.
 *
 * Camera settings switches sides. The form is settings-only, so the open resolves under settings
 * mode and evicts the view-only panels, while `also` brings the cameras list and the form's own
 * auto-open panel brings the live preview — one click onto the whole settings workflow. The list
 * rides `also` rather than the form's auto-open ids because the OTHER route to the form — the
 * player's Settings button — means "configure the camera I am watching" and wants no tree.
 *
 * The list is where the workflow starts, so it is the panel the stage holds. On a stage with
 * room for both that changes nothing; on one without, the entry lands on the camera tree rather
 * than on a form with no camera to show, and picking a camera there brings the form forward.
 * Once the operator is there it brings the LIST back when the stage has shed it or it was
 * closed, and does nothing while both show.
 */
export function openNavEntry(id: PanelId, state: NavOpenState, openPanel: OpenPanelFn): void {
    if (id === 'camera_settings') {
        if (!state.openIds.has('camera_settings')) {
            openPanel('camera_settings', undefined, { also: [PRIMARY_ID], focus: PRIMARY_ID });
            return;
        }
        if (!state.visibleIds.has(PRIMARY_ID)) {
            openPanel(PRIMARY_ID, undefined, { mode: PanelMode.Settings });
        }
        return;
    }
    if (isNavEntryOpen(id, state)) {
        return;
    }
    // Cameras forces the view side: without it, opening the already-open list would leave the
    // settings form up and the app still in settings mode.
    openPanel(
        id,
        getPanelEntry(id)?.config.launcherTabs.at(0)?.props,
        id === PRIMARY_ID ? { mode: PanelMode.View } : undefined,
    );
}

/** Opens the place of a launcher tile: its entry, on the tab that the tile stands for. */
export function openLauncherPlace(
    place: LauncherPlace, state: NavOpenState, openPanel: OpenPanelFn,
): void {
    if (place.props === undefined) {
        openNavEntry(place.entryId, state, openPanel);
        return;
    }
    // As the entry does: the cameras list opens on the view side, the only side with layouts.
    openPanel(
        place.entryId,
        place.props,
        place.entryId === PRIMARY_ID ? { mode: PanelMode.View } : undefined,
    );
}
